import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import { Pill } from '@/components/pills/Pill'
import NoData from '@/components/NoData'
import ChangeRequestCard from '@/views/VoiceAgents/components/ChangeRequestCard'
import { getUiStatus, isOpenRequest, UI_STATUS } from '@/views/VoiceAgents/changeRequestHelpers'
import { setFocusedTranscriptChangeRequest } from '@/reducers/voiceAgents/voiceAgents.redux'

import './AgentCommentsTab.scss'

// =============================================================================
// AgentCommentsTab — Call Explorer tab listing the voice-agent transcript
// comments for the open call (request_type='transcript'). Distinct from the
// legacy CallNotesTab "Comments" tab (Balto notes). The list is fetched by
// CallExplorerPage so the transcript marks stay populated even when this tab is
// closed; here we just read the call-scoped slice and render it.
//
// Read and write, not triage: comments are filed here and their text corrected
// here, but resolving one is queue work and belongs to Studio, so the cards drop
// Resolve/Reopen on this surface (AVR-877). The Resolved filter stays — a reader
// on the call still wants to see what has already been answered.
// =============================================================================

const EMPTY_COPY = {
  [UI_STATUS.OPEN]: 'No open comments. Select assistant text in the transcript to add one.',
  [UI_STATUS.RESOLVED]: 'No resolved comments yet.',
}

export const AgentCommentsTab = ({ callId }) => {
  const dispatch = useDispatch()
  const items = useSelector((state) => state.voiceAgents.transcriptChangeRequests.items)
  const loading = useSelector((state) => state.voiceAgents.loading.transcriptChangeRequests)
  const focusedId = useSelector((state) => state.voiceAgents.transcriptChangeRequests.focusedId)

  const [statusFilter, setStatusFilter] = useState(UI_STATUS.OPEN)

  // A deep-linked comment (focused from the by-user list) may be resolved; follow
  // the focused comment's status so it isn't hidden under the default Open filter.
  // Switching filters clears focus, so this can't fight a manual filter change.
  useEffect(() => {
    if (isNil(focusedId)) {
      return
    }
    const focused = items.find((request) => request.id === focusedId)
    if (focused) {
      setStatusFilter(getUiStatus(focused))
    }
  }, [focusedId])

  // Switching filters drops any selected comment so a stale highlight (card +
  // transcript mark) doesn't linger across — or back through — the Open/Resolved sets.
  const handleFilterChange = (next) => {
    if (next === statusFilter) {
      return
    }
    setStatusFilter(next)
    dispatch(setFocusedTranscriptChangeRequest(null))
  }

  const openCount = items.filter(isOpenRequest).length
  const resolvedCount = items.length - openCount
  const filteredItems = items.filter((request) => getUiStatus(request) === statusFilter)
  const isInitialLoading = loading && isEmpty(items)

  return (
    <div className="agent-comments-tab" data-testid="agent-comments-tab">
      <div className="agent-comments-tab__filters">
        <Pill
          clickable
          outlined
          selected={statusFilter === UI_STATUS.OPEN}
          onClick={() => handleFilterChange(UI_STATUS.OPEN)}
          dataTestId="agent-comments-filter-open"
        >
          Open ({openCount})
        </Pill>
        <Pill
          clickable
          outlined
          selected={statusFilter === UI_STATUS.RESOLVED}
          onClick={() => handleFilterChange(UI_STATUS.RESOLVED)}
          dataTestId="agent-comments-filter-resolved"
        >
          Resolved ({resolvedCount})
        </Pill>
      </div>

      {!isInitialLoading && isEmpty(filteredItems) && (
        <NoData headerText="No comments" text={EMPTY_COPY[statusFilter]} />
      )}

      {!isEmpty(filteredItems) && (
        <div className="agent-comments-tab__list" data-testid="agent-comments-list">
          {filteredItems.map((request) => (
            <ChangeRequestCard
              key={request.id}
              request={request}
              focused={request.id === focusedId}
              onClick={() => dispatch(setFocusedTranscriptChangeRequest(request.id))}
              callId={callId}
              hideStatusActions
            />
          ))}
        </div>
      )}
    </div>
  )
}
